// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/planning/drag-drop-voice-upload-plan.md

import React, { useState, useRef } from 'react';
import { Box, Alert, Typography } from '@mui/material';

const DragDropZone = ({
  onFilesSelected,
  accept = "audio/*",
  multiple = true,
  maxFiles = 5,
  maxSize = 50,
  disabled = false,
  children,
  sx = {}
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Validate files
  const validateFiles = (files) => {
    const fileArray = Array.from(files);
    
    // Check file count
    if (fileArray.length > maxFiles) {
      return { 
        valid: false, 
        error: `Maximum ${maxFiles} file(s) allowed. You selected ${fileArray.length} file(s).` 
      };
    }
    
    // Check file size and type
    const allowedTypes = ['audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/m4a', 'audio/flac', 'audio/ogg', 'audio/x-wav', 'audio/wave'];
    const allowedExtensions = ['.wav', '.mp3', '.m4a', '.flac', '.ogg', '.wma', '.aac', '.opus'];
    let totalSize = 0;
    
    for (const file of fileArray) {
      // Check file size
      if (file.size > maxSize * 1024 * 1024) {
        return { 
          valid: false, 
          error: `File "${file.name}" exceeds ${maxSize}MB limit (${Math.round(file.size / 1024 / 1024)}MB).` 
        };
      }
      
      totalSize += file.size;
      
      // Check file type
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      const hasValidMimeType = allowedTypes.includes(file.type);
      const hasValidExtension = allowedExtensions.includes(fileExtension);
      
      if (!hasValidMimeType && !hasValidExtension) {
        return { 
          valid: false, 
          error: `Unsupported file type: "${file.name}". Please upload audio files only (WAV, MP3, M4A, FLAC, OGG).` 
        };
      }
    }
    
    // Check total size (max 250MB for 5 files: 5 × 50MB)
    if (totalSize > 250 * 1024 * 1024) {
      return { 
        valid: false, 
        error: `Total file size (${Math.round(totalSize / 1024 / 1024)}MB) exceeds 250MB limit.` 
      };
    }
    
    return { valid: true, files: fileArray };
  };

  // Handle drag enter
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files.length === 0) return;
    
    const validation = validateFiles(files);
    
    if (validation.valid) {
      setError(null);
      onFilesSelected(validation.files);
    } else {
      setError(validation.error);
      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle click to browse
  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files.length === 0) return;
    
    const validation = validateFiles(files);
    
    if (validation.valid) {
      setError(null);
      onFilesSelected(validation.files);
    } else {
      setError(validation.error);
      setTimeout(() => setError(null), 5000);
    }
    
    // Reset input to allow selecting same file again
    e.target.value = null;
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="Drop zone for voice files"
      sx={{
        border: `2px dashed ${isDragOver ? 'primary.main' : 'grey.300'}`,
        borderRadius: 2,
        p: 1.5,
        bgcolor: isDragOver ? 'action.hover' : 'background.paper',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease-in-out',
        transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
        opacity: disabled ? 0.6 : 1,
        '&:focus': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
        ...sx
      }}
    >
      {children}
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
        disabled={disabled}
      />
      
      {/* Error message */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mt: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default DragDropZone;

